import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {DigitalSignageSubPanel} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/components/digital-signage-sub-panel/digital-signage-sub-panel';
import {Text, TextSize} from '@Components/text';
import type {PosterProductDataForProcessDesign} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.types';
import {usePosterProduct, usePrintProduct} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel.hooks';
import {useCopyButton} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.hooks';
import type {PublishingYodeckPanelProps} from './publishing-yodeck-panel.types';
import type {DisplayCodeForSignage} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {fetchDisplayCodeAsync} from '@Panels/poster-publishing-options-panel/poster-publishing-options-panel-library';
import {DigitalSignageOption} from '@Panels/poster-publishing-options-panel/components/publishing-digital-signage-list/publishing-digital-signage-list.types';

export function PublishingYodeckPanel({...props}: PublishingYodeckPanelProps): ReactElement {
  const [displayCode, setDisplayCode] = useState('');
  const [generatedIframe, setGeneratedIframe] = useState('');
  const [clicked, setClicked] = useState(false);
  const premiumProductDataForPrint = usePrintProduct();
  const premiumProductDataForPoster = usePosterProduct();
  const productUsed: PosterProductDataForProcessDesign | undefined =
    premiumProductDataForPoster && Object.keys(premiumProductDataForPoster).length > 0 ? premiumProductDataForPoster : premiumProductDataForPrint;

  useEffect(() => {
    void fetchDisplayCodeAsync(productUsed).then((response: DisplayCodeForSignage | null) => {
      if (response !== null) {
        setDisplayCode(response.displayCode);
        setGeneratedIframe(response.generatedIframe);
      }
    });
  }, [displayCode]);

  useEffect(() => {
    setTimeout(() => {
      setClicked(false);
    }, 3000);
  }, [clicked]);

  const listItemElement: ReactElement = (
    <div className="content-sub-text">
      <Text val={window.i18next.t('pmwjs_yodeck_follow_steps')} size={TextSize.XSMALL} />
      <div className="flexbox flex-v-row color-supplementary-text">
        <Text dangerouslySetInnerHTML className="spacing-p-b-2" val={window.i18next.t('pmwjs_yodeck_step1')} size={TextSize.XSMALL} />
        <Text className="spacing-p-b-2" val={window.i18next.t('pmwjs_yodeck_step2')} size={TextSize.XSMALL} />
        <Text val={window.i18next.t('pmwjs_yodeck_step3')} size={TextSize.XSMALL} />
      </div>
    </div>
  );

  const onCopyLink = (): void => {
    void window.PMW.writeLocal('cart/addOrderUsedByDigitalSignage', {
      orderId: productUsed?.orderId,
      type: DigitalSignageOption.YODECK,
    });
  };

  return (
    <DigitalSignageSubPanel
      panelHeading={window.i18next.t('pmwjs_yodeck')}
      heading={window.i18next.t('pmwjs_yodeck_digital_signage')}
      showButton
      showInputBar
      webpageLink={generatedIframe}
      inputBarIcon="icon-code"
      listItem={listItemElement}
      onClose={props.onClose}
      onClick={() => {
        window.PMW.copyTextToClipboard(generatedIframe);
        onCopyLink();
      }}
      buttonComponent={useCopyButton(clicked, generatedIframe, setClicked, onCopyLink)}
      imgSrc="publishing-yodeck.png"
    />
  );
}
